import {
  denormalizeRecord,
  UkhabUnknownSchemaError,
  type CoreoRecordInput,
  type UkhabRecordSchemaId,
} from '@natural-apptitude/ukhab-lib';
import {
  computeBaselineMetricSummaryFromMappedData,
  type BaselineMetricSummary,
} from './baselineMetricSummary';
import { ukhabMapBaselineRawData } from './ukhabExpressionPrimitives';

const CURRENT_UKHAB_TEMPLATE = 'ukhab4.0' as const;

export type UkhabRecordExpressionRole = 'baseline' | 'presurvey';

export type UkhabRecordExpressionOutputs = {
  symbology: string | null;
  pcode: string;
  lineColor: string;
  lineDashColor: string;
  combinedCode: string;
} & BaselineMetricSummary;

const toRecordInput = (value: unknown): CoreoRecordInput | null => {
  if (value == null || typeof value !== 'object' || !('data' in value)) {
    return null;
  }
  const record = value as CoreoRecordInput & {
    id?: number | null;
    projectId?: number;
    isNew?: boolean;
    parentId?: number | null;
    geometry?: unknown;
  };
  // Preserve host snapshot fields for forward-compat (ukhab-lib currently uses data/surveyId).
  return {
    data: record.data ?? {},
    surveyId: record.surveyId,
    id: record.id,
    projectId: record.projectId,
    isNew: record.isNew,
    parentId: record.parentId,
    geometry: record.geometry,
  } as CoreoRecordInput;
};

const resolveSchemaId = (role: UkhabRecordExpressionRole): UkhabRecordSchemaId => {
  return `${CURRENT_UKHAB_TEMPLATE}:${role}`;
};

export const denormalizeUkhabRecord = (
  recordArg: unknown,
  role: UkhabRecordExpressionRole,
): UkhabRecordExpressionOutputs | null => {
  const record = toRecordInput(recordArg);
  if (!record) {
    return null;
  }

  const schemaId = resolveSchemaId(role);

  try {
    const denorm = denormalizeRecord(record, schemaId);
    const base: UkhabRecordExpressionOutputs = {
      symbology: denorm.symbology,
      pcode: denorm.pcode,
      lineColor: denorm.lineColor,
      lineDashColor: denorm.lineDashColor,
      combinedCode: denorm.combinedCode,
    };

    if (role === 'baseline') {
      const metric = computeBaselineMetricSummaryFromMappedData(ukhabMapBaselineRawData(record.data ?? {}));
      return {
        ...base,
        ...metric,
      };
    }

    return base;
  } catch (err) {
    if (err instanceof UkhabUnknownSchemaError) {
      console.warn('UKHab denormalize failed: unknown schema', {
        schemaId,
        surveyId: record.surveyId,
      });
      return null;
    }
    throw err;
  }
};
